import {TextInput} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Center} from 'src/design/atoms/Center';
import {Divider} from 'src/design/atoms/Divider';
import {List} from 'src/design/atoms/List';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {Switch} from 'src/design/atoms/Switch';
import {Text} from 'src/design/atoms/Text';
import {Title} from 'src/design/atoms/Title';
import {IconAlertCircle} from '@tabler/icons-react';
import {useEffect, useMemo, useState} from 'react';
import {Controller, useForm, useWatch} from 'react-hook-form';
import {useParams} from 'react-router-dom';
import type {
  WorkflowDefinitionPhaseResponse,
  WorkflowDefinitionRequest,
  WorkflowDefinitionResponse,
  WorkflowDefinitionTriggerResponse,
} from 'src/api/ProactApiClient';
import {
  FeatureFlagEnum,
  ModuleEnum,
  NotificationRuleTypeEnum,
  PermissionOperationEnum,
  WorkflowDefinitionTriggerTypeEnum,
} from 'src/api/ProactApiClient';
import {SaveButton} from 'src/design/molecules/SaveButton';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';

import type {PageNavigationConfig} from 'src/app/layout/pageNavigationConfig';
import {buildPageNavigation} from 'src/app/layout/pageNavigationConfig';
import {ProjectSelectBox} from 'src/app/databound-elements/ProjectSelectBox';
import {SidebarLinkWorkflows} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import type {IWorkflowDesignerMetadata} from 'src/app/workflows/types';
import {
  MapWorkflowDefinitionResponseToWorkflowDefinitionRequest,
  MapWorkflowDefinitionTriggerTypeToModule,
  WorkflowDefinitionPillConfig,
  WorkflowDocumentTitle,
} from 'src/app/workflows/workflowHelpers';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {
  useCreateWorkflowDefinitionMutation,
  useGetWorkflowDefinitionQuery,
  useUpdateWorkflowDefinitionMutation,
} from 'src/redux/queries/workflowDefinition';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {ISelectOption} from 'src/types/Form';
import {TriggerList} from './components/TriggerList';
import {PhaseList} from './components/PhaseList';
import {useWorkflowValidation} from './components/useWorkflowValidation';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const navigationConfig: PageNavigationConfig = {
  entityName: 'Workflow',
  entityNamePlural: 'Workflows',
  listPath: PathUtility.WorkflowsDashboard('manage'),
  viewPath: id => PathUtility.WorkflowViewDefinition(String(id)),
};

const emptyDefinition: WorkflowDefinitionResponse = {
  name: '',
  active: true,
  phases: [],
  triggers: [],
} as WorkflowDefinitionResponse;

interface WorkflowGeneralFormValues {
  name: string;
  active: boolean;
}

export const EditWorkflowDefinitionPage = () => {
  useSetDocumentTitle(WorkflowDocumentTitle);
  const {id} = useParams();
  const isEditMode = id !== undefined;
  const isCreateMode = !isEditMode;

  const {setAlert} = useAlert();
  const navigate = useAppNavigate();
  const [isSavePending, setIsSavePending] = useState(false);
  const [localData, setLocalData] = useState<
    WorkflowDefinitionResponse | undefined
  >(undefined);

  const [createWorkflowDefinition] = useCreateWorkflowDefinitionMutation();
  const [updateWorkflowDefinition] = useUpdateWorkflowDefinitionMutation();

  const parsedId = id ? parseInt(id) : -1;

  const {data: fetchedData, isLoading} = useGetWorkflowDefinitionQuery(
    parsedId,
    {skip: !isEditMode}
  );

  const {control, getValues, reset} = useForm<WorkflowGeneralFormValues>({
    defaultValues: {name: emptyDefinition.name, active: emptyDefinition.active},
  });

  useEffect(() => {
    if (isEditMode && !isLoading && fetchedData) {
      reset({name: fetchedData.name ?? '', active: fetchedData.active ?? true});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, isLoading, id]);

  const watchedName = useWatch({control, name: 'name'});
  const watchedActive = useWatch({control, name: 'active'});

  // Use local edits when present, fall back to fetched data, then empty definition
  const data: WorkflowDefinitionResponse = {
    ...(localData ?? fetchedData ?? emptyDefinition),
    name: watchedName,
    active: watchedActive,
  };

  const isSingleTriggerWorkflow = data.triggers.length === 1;
  const primaryTrigger = isSingleTriggerWorkflow ? data.triggers[0] : undefined;

  const metadata: IWorkflowDesignerMetadata = {
    isSingleTriggerWorkflow,
    primaryTrigger,
    isScheduled:
      primaryTrigger?.type === WorkflowDefinitionTriggerTypeEnum.Schedule,
    module: MapWorkflowDefinitionTriggerTypeToModule(primaryTrigger?.type),
    ruleType: NotificationRuleTypeEnum.ChangeEvent,
    allPhases: data.phases,
    currentPhaseIndex: 0,
  };

  const validation = useWorkflowValidation(data);

  const submitUpdate = async () => {
    setIsSavePending(true);
    try {
      const model: WorkflowDefinitionRequest =
        MapWorkflowDefinitionResponseToWorkflowDefinitionRequest({
          ...data,
          ...getValues(),
        });
      const response = await updateWorkflowDefinition(model).unwrap();
      setAlert(
        `Workflow ${response?.name} was successfully updated.`,
        'success'
      );
      navigate(PathUtility.WorkflowViewDefinition(String(response?.id)), {
        replace: true,
      });
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsSavePending(false);
  };

  const submitCreate = async () => {
    setIsSavePending(true);
    try {
      const model: WorkflowDefinitionRequest =
        MapWorkflowDefinitionResponseToWorkflowDefinitionRequest({
          ...data,
          ...getValues(),
        });
      const response = await createWorkflowDefinition(model).unwrap();
      setAlert(
        `Workflow ${response?.name} was successfully created.`,
        'success'
      );
      navigate(PathUtility.WorkflowViewDefinition(String(response?.id)), {
        replace: true,
      });
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsSavePending(false);
  };

  const handleProjectChange = (option: ISelectOption | null) => {
    const projectId = option?.id === '' ? undefined : Number(option?.id);
    setLocalData({...data, projectId: projectId});
  };

  const handleTriggerChange = (
    triggers: WorkflowDefinitionTriggerResponse[]
  ) => {
    setLocalData({...data, triggers});
  };

  const handlePhasesChange = (phases: WorkflowDefinitionPhaseResponse[]) => {
    setLocalData({...data, phases});
  };

  const handleSubmit = () => {
    if (validation.isValid) {
      if (data?.id !== undefined) {
        void submitUpdate();
      } else {
        void submitCreate();
      }
    }
  };

  const {goBack: handleCancel} = useBackNavigation(
    isEditMode && id
      ? PathUtility.WorkflowViewDefinition(id)
      : PathUtility.WorkflowsDashboard('manage')
  );

  const navigation = useMemo(
    () =>
      buildPageNavigation(navigationConfig, {
        mode: isEditMode ? 'edit' : 'create',
        id,
        itemName: data?.name,
      }),
    [isEditMode, id, data?.name]
  );

  const pills = useMemo(
    () => WorkflowDefinitionPillConfig(data?.active ?? true),
    [data?.active]
  );

  if (isLoading) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.Workflows}>
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </ContentWrapper>
    );
  }

  const pageTitle = isCreateMode ? 'New Workflow' : data?.name ?? 'Workflow';

  const allValidationErrors = [
    ...Object.values(validation.errors),
    ...Object.values(validation.sectionErrors).flat(),
  ];

  const validationSummary = !validation.isValid && (
    <Alert
      icon={<IconAlertCircle size={16} />}
      title="Validation Errors"
      color="red">
      <List size="sm">
        {allValidationErrors.map((error, i) => (
          <List.Item key={i}>{error}</List.Item>
        ))}
      </List>
    </Alert>
  );

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Workflows}>
      <Stack gap="lg">
        <BackBar
          fallbackTo={navigation.fallbackTo}
          fallbackLabel={navigation.fallbackLabel}
          breadcrumbs={navigation.breadcrumbs}
        />

        <PageHeader>
          <PageHeader.Title
            icon={SidebarLinkWorkflows.icon}
            subtitle={data?.internalIdentifier}>
            {pageTitle}
          </PageHeader.Title>

          <PageHeader.Badges>
            {pills.map((pill, index) => (
              <PageHeader.Badge key={index} color={pill.color} variant="light">
                {pill.text}
              </PageHeader.Badge>
            ))}
            {isEditMode && (
              <PageHeader.Badge color="yellow" variant="light">
                Editing
              </PageHeader.Badge>
            )}
          </PageHeader.Badges>

          <PageHeader.Actions>
            <PageHeader.Action variant="cancel" onClick={handleCancel}>
              Cancel
            </PageHeader.Action>
            <PageHeader.Action
              variant={isCreateMode ? 'create' : 'save'}
              onClick={handleSubmit}
              loading={isSavePending}
              disabled={!validation.isValid}>
              {isCreateMode ? 'Create Workflow' : 'Save Changes'}
            </PageHeader.Action>
          </PageHeader.Actions>
        </PageHeader>

        <Stack gap="md">
          <div>
            <Title order={4}>General</Title>
            <Text size="sm" c="dimmed">
              Basic workflow settings including name, project scope, and status.
            </Text>
          </div>
          <Controller
            name="name"
            control={control}
            render={({field}) => (
              <TextInput
                label="Name"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                required
                error={validation.errors['name']}
              />
            )}
          />
          <ProjectSelectBox
            label="Project"
            value={data.projectId ? String(data.projectId) : ''}
            onSelectChangeCallback={handleProjectChange}
          />
          <Controller
            name="active"
            control={control}
            render={({field}) => (
              <Switch
                label="Active"
                checked={field.value}
                onChange={e => field.onChange(e.currentTarget.checked)}
                onBlur={field.onBlur}
              />
            )}
          />
        </Stack>

        <Divider />

        <TriggerList
          triggers={data.triggers}
          onTriggersChange={handleTriggerChange}
          projectId={data.projectId}
        />

        <Divider />

        <PhaseList
          phases={data.phases}
          onPhasesChange={handlePhasesChange}
          metadata={metadata}
        />

        {validationSummary}

        <SaveButton onClick={handleSubmit} loading={isSavePending}>
          {isCreateMode ? 'Create Workflow' : 'Save Changes'}
        </SaveButton>
      </Stack>
    </ContentWrapper>
  );
};

// Register the routes with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'form',
  path: PathUtility.WorkflowCreate(),
  element: <EditWorkflowDefinitionPage />,
  module: ModuleEnum.Workflows,
  operation: PermissionOperationEnum.Create,
  isPrivate: true,
});

routeRegistry.registerPageRoute({
  backNavigation: 'form',
  path: PathUtility.WorkflowEdit(':id'),
  element: <EditWorkflowDefinitionPage />,
  module: ModuleEnum.Workflows,
  operation: PermissionOperationEnum.Update,
  isPrivate: true,
});

export default EditWorkflowDefinitionPage;
